import React, { useRef } from 'react';
import { useEvent } from 'react-use';
import styled from 'styled-components';

/**
 * Hook that detects clicks outside of the passed ref
 */
function useOutsideClick(
	ref: React.MutableRefObject<HTMLDivElement | null>,
	buttonRefs: React.MutableRefObject<HTMLElement | null>[],
	handler: (event: React.MouseEvent) => void
) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const eventHandler = (event: any) => {
		if (ref.current !== null) {
			if (buttonRefs.length === 0) {
				if (!ref.current.contains(event.target)) {
					handler(event);
				}
			} else {
				if (
					!ref.current.contains(event.target) &&
					buttonRefs
						.map(({ current }) => current)
						.every(button => !button?.contains(event.target))
				) {
					handler(event);
				}
			}
		}
	};

	useEvent('mousedown', eventHandler);
	// useEvent('focusout', eventHandler, ref.current);
}

// Passing a ref for the triggering button will exclude said button from an outside click trigger
export type CaptureOuterClickProps = React.HTMLAttributes<HTMLDivElement> & {
	children: React.ReactNode;
	onMouseDown?: (event: React.MouseEvent<Element, MouseEvent>) => void;
	buttonRefs?: React.MutableRefObject<HTMLElement | null>[];
	classString?: string;
	useDefaultHandler?: boolean;
	containerRef?: React.RefObject<HTMLDivElement>;
	styles?: React.CSSProperties;
};

const defaultHandler = (event: React.MouseEvent<Element, MouseEvent>) => {
	if (event && event.relatedTarget && event.relatedTarget.dispatchEvent) {
		if (document.createEvent !== undefined) {
			const clickEvent = document.createEvent('MouseEvents');

			clickEvent.initMouseEvent(
				'click',
				true,
				true,
				window,
				0,
				0,
				0,
				0,
				0,
				false,
				false,
				false,
				false,
				0,
				null
			);

			event.relatedTarget.dispatchEvent(clickEvent);
		}
	}
};

export const CaptureOuterClick: React.FC<CaptureOuterClickProps> = ({
	children,
	useDefaultHandler,
	onMouseDown,
	buttonRefs = [],
	className,
	classString = className,
	containerRef,
	styles,
	...props
}) => {
	const wrapperRef = useRef<HTMLDivElement>(null);

	useOutsideClick(containerRef ?? wrapperRef, buttonRefs, e => {
		if (onMouseDown) {
			onMouseDown(e);
		}

		if (useDefaultHandler || !onMouseDown) {
			defaultHandler(e);
		}
	});

	return (
		<Container
			className={`${classString ?? ''}`}
			ref={containerRef ?? wrapperRef}
			styles={styles}
			{...props}
		>
			{children}
		</Container>
	);
};

interface ContainerProps {
	styles?: React.CSSProperties;
}

const Container = styled.div<ContainerProps>`
	position: relative;
	display: ${props => props.styles?.display ?? 'block'};
	flex-wrap: ${props => props.styles?.flexWrap ?? 'wrap'};
	align-items: ${props => props.styles?.alignItems ?? 'stretch'};
	width: ${props => props.styles?.width ?? 'auto'};
`;
